import * as React from 'react';
import { useState } from 'react';
import { Button, Spinner } from '@patternfly/react-core';
import { BoltIcon } from '@patternfly/react-icons';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { CheckoutPaymentResponse } from '@app/utils/utils';
import { AppModal } from '@app/components/AppModal';
import { LogoBadge } from '@app/components/LogoBadge';
import { GradientButton } from '@app/components/GradientButton';
import { colors, formResetStyle, radii } from '@app/components/designTokens';

let _stripePromise: Promise<Stripe | null> | null = null;
export function getStripe(): Promise<Stripe | null> {
    if (!_stripePromise) {
        _stripePromise = loadStripe(process.env['STRIPE_PUB_KEY'] + '');
    }
    return _stripePromise;
}

interface SubscribePaymentModalProps {
    isOpen: boolean;
    checkout: CheckoutPaymentResponse | null;
    tierLabel: string;
    /** True while the backend webhook is creating the subscription after card confirmation. */
    finalizing?: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const SubscribePaymentModal: React.FC<SubscribePaymentModalProps> = ({
    isOpen,
    checkout,
    tierLabel,
    finalizing = false,
    onClose,
    onSuccess
}) => {
    if (!checkout) return null;

    const isTrial = checkout.mode === 'setup';
    const subhead = finalizing
        ? isTrial
            ? 'Finalizing your trial — this only takes a moment…'
            : 'Finalizing your subscription — this only takes a moment…'
        : isTrial
          ? 'Start your trial — no charge today'
          : 'Card details are processed securely by Stripe';

    return (
        <AppModal isOpen={isOpen} onClose={onClose} width="md">
            <div style={{ padding: '40px 32px 32px' }}>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <LogoBadge size={48}>
                        <BoltIcon style={{ fontSize: 24, color: colors.boltGold }} />
                    </LogoBadge>
                    <h1 style={{ color: colors.title, fontSize: '24px', fontWeight: 700, margin: 0, marginBottom: '6px', letterSpacing: '-0.5px' }}>
                        {finalizing ? (isTrial ? 'Starting your trial' : 'Confirming your subscription') : `Subscribe to ${tierLabel}`}
                    </h1>
                    <p style={{ color: colors.subtle, fontSize: '14px', margin: 0 }}>
                        {subhead}
                    </p>
                </div>
                {/* Card confirmed — the subscription is created by the setup_intent.succeeded webhook
                    (~1–2s), which the parent polls for. Swap the card form for a finalizing state so the
                    customer isn't left staring at the form after they hit "Start trial". */}
                {finalizing ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', padding: '24px 0 8px' }}>
                        <Spinner size="lg" aria-label="Finalizing" />
                        <span style={{ color: colors.subtle, fontSize: '14px' }}>
                            {isTrial ? 'Finalizing your trial…' : 'Finalizing your subscription…'}
                        </span>
                    </div>
                ) : (
                    <Elements stripe={getStripe()} options={{ clientSecret: checkout.clientSecret }}>
                        <PaymentForm mode={checkout.mode} onSuccess={onSuccess} onCancel={onClose} />
                    </Elements>
                )}
            </div>
        </AppModal>
    );
};

interface PaymentFormProps {
    mode: 'payment' | 'setup';
    onSuccess: () => void;
    onCancel: () => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ mode, onSuccess, onCancel }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setSubmitting(true);
        setErrorMessage(null);

        const returnUrl = window.location.origin + '/plan?checkout=success';

        const result =
            mode === 'setup'
                ? await stripe.confirmSetup({
                      elements,
                      confirmParams: { return_url: returnUrl },
                      redirect: 'if_required'
                  })
                : await stripe.confirmPayment({
                      elements,
                      confirmParams: { return_url: returnUrl },
                      redirect: 'if_required'
                  });

        if (result.error) {
            setErrorMessage(result.error.message || 'Payment failed');
            setSubmitting(false);
            return;
        }

        onSuccess();
    };

    return (
        <form onSubmit={handleSubmit} style={formResetStyle}>
            <PaymentElement />
            {errorMessage && (
                <div style={{
                    marginTop: '12px',
                    color: colors.danger,
                    fontSize: '14px',
                    padding: '12px 14px',
                    background: '#fef2f2',
                    border: `1px solid #fecaca`,
                    borderRadius: radii.input
                }}>
                    {errorMessage}
                </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
                <Button variant="link" onClick={onCancel} isDisabled={submitting} style={{ color: colors.subtle, fontWeight: 600 }}>
                    Cancel
                </Button>
                <GradientButton
                    type="submit"
                    isDisabled={!stripe || !elements || submitting}
                    isLoading={submitting}
                >
                    {submitting ? 'Processing…' : mode === 'setup' ? 'Start trial' : 'Subscribe'}
                </GradientButton>
            </div>
        </form>
    );
};
